import { useState, useEffect } from 'react'
import {
  Shield,
  Building2,
  Calendar,
  Clock,
  Save,
  Loader2,
  AlertCircle,
  CheckCircle,
  XCircle,
  HelpCircle,
} from 'lucide-react'
import { legalAPI } from '@/utils/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { toast } from 'sonner'

interface RSVCompany {
  id: number
  name: string
  code: string
  email: string
  portal_url: string
}

interface LegalProcess {
  id: number
  case: number
  case_number: string
  rsv_company: RSVCompany | null
  rsv_policy_number: string
  rsv_status: string
  rsv_status_display: string
  rsv_reference: string
  court_name: string
  court_case_number: string
  mahnschreiben_sent_at: string | null
  klage_filed_at: string | null
  next_deadline: string | null
  next_hearing_date: string | null
  internal_notes: string
}

interface LegalProcessCardProps {
  caseId: number
  onUpdate?: () => void
}

const RSV_STATUS_OPTIONS = [
  { value: 'not_applicable', label: 'Nicht zutreffend', icon: HelpCircle, color: 'text-slate-500' },
  { value: 'pending', label: 'Angefragt', icon: Clock, color: 'text-amber-500' },
  { value: 'approved', label: 'Deckungszusage', icon: CheckCircle, color: 'text-green-500' },
  { value: 'denied', label: 'Abgelehnt', icon: XCircle, color: 'text-red-500' },
  { value: 'partial', label: 'Teildeckung', icon: AlertCircle, color: 'text-orange-500' },
]

function formatDate(dateString: string | null): string {
  if (!dateString) return '—'
  const date = new Date(dateString)
  return date.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}

function formatDateTime(dateString: string | null): string {
  if (!dateString) return '—'
  const date = new Date(dateString)
  return date.toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function LegalProcessCard({ caseId, onUpdate }: LegalProcessCardProps) {
  const [legalProcess, setLegalProcess] = useState<LegalProcess | null>(null)
  const [rsvCompanies, setRsvCompanies] = useState<RSVCompany[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [hasChanges, setHasChanges] = useState(false)

  // Form state - use 'none' as sentinel value for no RSV company
  const [rsvCompanyId, setRsvCompanyId] = useState<string>('none')
  const [rsvPolicyNumber, setRsvPolicyNumber] = useState('')
  const [rsvStatus, setRsvStatus] = useState('not_applicable')
  const [rsvReference, setRsvReference] = useState('')
  const [courtName, setCourtName] = useState('')
  const [courtCaseNumber, setCourtCaseNumber] = useState('')
  const [internalNotes, setInternalNotes] = useState('')

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true)
      try {
        // Fetch RSV companies and legal process in parallel
        const [companiesRes, processRes] = await Promise.all([
          legalAPI.getRSVCompanies(),
          legalAPI.getLegalProcess(caseId),
        ])

        setRsvCompanies(companiesRes.companies || [])

        const process = processRes.legal_process
        setLegalProcess(process)

        // Initialize form state
        setRsvCompanyId(process.rsv_company?.id?.toString() || 'none')
        setRsvPolicyNumber(process.rsv_policy_number || '')
        setRsvStatus(process.rsv_status || 'not_applicable')
        setRsvReference(process.rsv_reference || '')
        setCourtName(process.court_name || '')
        setCourtCaseNumber(process.court_case_number || '')
        setInternalNotes(process.internal_notes || '')
        setHasChanges(false)
      } catch (error) {
        console.error('Failed to fetch legal process:', error)
        toast.error('Fehler beim Laden des Verfahrensstatus')
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()
  }, [caseId])

  // Track changes
  useEffect(() => {
    if (!legalProcess) return
    const changed =
      rsvCompanyId !== (legalProcess.rsv_company?.id?.toString() || 'none') ||
      rsvPolicyNumber !== (legalProcess.rsv_policy_number || '') ||
      rsvStatus !== (legalProcess.rsv_status || 'not_applicable') ||
      rsvReference !== (legalProcess.rsv_reference || '') ||
      courtName !== (legalProcess.court_name || '') ||
      courtCaseNumber !== (legalProcess.court_case_number || '') ||
      internalNotes !== (legalProcess.internal_notes || '')
    setHasChanges(changed)
  }, [legalProcess, rsvCompanyId, rsvPolicyNumber, rsvStatus, rsvReference, courtName, courtCaseNumber, internalNotes])

  const handleSave = async () => {
    setIsSaving(true)
    try {
      const data: Record<string, unknown> = {
        rsv_policy_number: rsvPolicyNumber,
        rsv_status: rsvStatus,
        rsv_reference: rsvReference,
        court_name: courtName,
        court_case_number: courtCaseNumber,
        internal_notes: internalNotes,
      }

      // Only include rsv_company_id if a company is selected (not 'none')
      if (rsvCompanyId && rsvCompanyId !== 'none') {
        data.rsv_company_id = parseInt(rsvCompanyId)
      } else {
        data.rsv_company_id = null
      }

      const response = await legalAPI.updateLegalProcess(caseId, data)
      setLegalProcess(response.legal_process)
      setHasChanges(false)
      toast.success('Verfahrensstatus gespeichert')
      onUpdate?.()
    } catch (error) {
      console.error('Failed to save legal process:', error)
      toast.error('Fehler beim Speichern')
    } finally {
      setIsSaving(false)
    }
  }

  const getStatusOption = (status: string) => {
    return RSV_STATUS_OPTIONS.find((opt) => opt.value === status) || RSV_STATUS_OPTIONS[0]
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 text-slate-400 animate-spin" />
      </div>
    )
  }

  const statusOption = getStatusOption(rsvStatus)
  const StatusIcon = statusOption.icon

  return (
    <div className="space-y-6">
      {/* RSV Section */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <Shield className="h-5 w-5 text-blue-500" />
            Rechtschutzversicherung
          </h3>
          <Badge
            variant="outline"
            className={`flex items-center gap-1 ${statusOption.color}`}
          >
            <StatusIcon className="h-3.5 w-3.5" />
            {statusOption.label}
          </Badge>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="rsv_company" className="text-sm text-slate-600">
              RSV Gesellschaft
            </Label>
            <Select value={rsvCompanyId} onValueChange={setRsvCompanyId}>
              <SelectTrigger id="rsv_company" className="mt-1">
                <SelectValue placeholder="RSV auswählen..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Keine RSV</SelectItem>
                {rsvCompanies.map((company) => (
                  <SelectItem key={company.id} value={company.id.toString()}>
                    {company.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="rsv_status" className="text-sm text-slate-600">
              Status
            </Label>
            <Select value={rsvStatus} onValueChange={setRsvStatus}>
              <SelectTrigger id="rsv_status" className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {RSV_STATUS_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    <span className="flex items-center gap-2">
                      <option.icon className={`h-4 w-4 ${option.color}`} />
                      {option.label}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="rsv_policy_number" className="text-sm text-slate-600">
              Versicherungsscheinnummer
            </Label>
            <Input
              id="rsv_policy_number"
              value={rsvPolicyNumber}
              onChange={(e) => setRsvPolicyNumber(e.target.value)}
              placeholder="z.B. RSV-12345678"
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="rsv_reference" className="text-sm text-slate-600">
              RSV Aktenzeichen
            </Label>
            <Input
              id="rsv_reference"
              value={rsvReference}
              onChange={(e) => setRsvReference(e.target.value)}
              placeholder="z.B. 2025-0001234"
              className="mt-1"
            />
          </div>
        </div>
      </div>

      {/* Court Section */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
        <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2 mb-4">
          <Building2 className="h-5 w-5 text-purple-500" />
          Gericht
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="court_name" className="text-sm text-slate-600">
              Gericht
            </Label>
            <Input
              id="court_name"
              value={courtName}
              onChange={(e) => setCourtName(e.target.value)}
              placeholder="z.B. AG Frankfurt am Main"
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="court_case_number" className="text-sm text-slate-600">
              Aktenzeichen
            </Label>
            <Input
              id="court_case_number"
              value={courtCaseNumber}
              onChange={(e) => setCourtCaseNumber(e.target.value)}
              placeholder="z.B. 123 C 456/25"
              className="mt-1 font-mono"
            />
          </div>
        </div>

        {/* Key Dates */}
        {legalProcess && (legalProcess.mahnschreiben_sent_at || legalProcess.klage_filed_at || legalProcess.next_hearing_date) && (
          <div className="mt-4 pt-4 border-t border-slate-100">
            <h4 className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
              <Calendar className="h-4 w-4 text-slate-400" />
              Termine & Fristen
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              {legalProcess.mahnschreiben_sent_at && (
                <div>
                  <span className="text-slate-500">Mahnschreiben:</span>
                  <span className="ml-2 text-slate-900">{formatDate(legalProcess.mahnschreiben_sent_at)}</span>
                </div>
              )}
              {legalProcess.klage_filed_at && (
                <div>
                  <span className="text-slate-500">Klage eingereicht:</span>
                  <span className="ml-2 text-slate-900">{formatDate(legalProcess.klage_filed_at)}</span>
                </div>
              )}
              {legalProcess.next_hearing_date && (
                <div>
                  <span className="text-slate-500">Nächster Termin:</span>
                  <span className="ml-2 text-slate-900 font-medium">{formatDateTime(legalProcess.next_hearing_date)}</span>
                </div>
              )}
              {legalProcess.next_deadline && (
                <div>
                  <span className="text-slate-500">Nächste Frist:</span>
                  <span className="ml-2 text-red-600 font-medium">{formatDate(legalProcess.next_deadline)}</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Internal Notes */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
        <h3 className="text-lg font-semibold text-slate-900 mb-4">
          Interne Notizen
        </h3>
        <Textarea
          value={internalNotes}
          onChange={(e) => setInternalNotes(e.target.value)}
          placeholder="Interne Notizen zum Verfahren (nicht sichtbar für Mandanten)..."
          className="min-h-[80px]"
        />
      </div>

      {/* Save Button */}
      {hasChanges && (
        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={isSaving} className="gap-2">
            {isSaving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            Speichern
          </Button>
        </div>
      )}
    </div>
  )
}
